const normalizeLineBreaks = (value = '') => String(value || '')
  .replace(/\r\n/g, '\n')
  .replace(/\r/g, '\n')
  .replace(/\u2028/g, '\n')
  .replace(/\u2029/g, '\n');

const cleanTagToken = (value = '') =>
  String(value || '')
    .trim()
    .replace(/^\[+/, '')
    .replace(/\]+$/, '')
    .trim();

const parseKwTags = (line = '') => {
  const match = String(line || '').match(/^\s*kw::\s*(.+)\s*$/i);
  if (!match) return [];
  return (match[1] || '')
    .split(/\s*-\s*/)
    .map(cleanTagToken)
    .filter(Boolean);
};

const DATE_TOKEN_RE = /\b(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\.?\s+\d{1,2}(?:,\s*\d{2,4})?)\b/i;

const parseDateTokenToIso = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const tokenMatch = raw.match(DATE_TOKEN_RE);
  if (!tokenMatch) return '';
  const parsed = new Date(tokenMatch[1]);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString();
};

const looksLikeDate = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (/\b\d{4}-\d{1,2}-\d{1,2}\b/.test(raw)) return true;
  if (/\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/.test(raw)) return true;
  if (/\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+\d{1,2}(?:,\s*\d{2,4})?\b/i.test(raw)) return true;
  return false;
};

const extractFirstDateIso = (text = '') => {
  const lines = normalizeLineBreaks(text).split('\n');
  for (const line of lines) {
    if (!looksLikeDate(line)) continue;
    const parsed = parseDateTokenToIso(line);
    if (parsed) return parsed;
  }
  return parseDateTokenToIso(text);
};

const normalizeNoteBody = (lines = []) => {
  const out = [];
  let lastWasBlank = false;

  lines.forEach((line) => {
    const normalizedLine = String(line || '')
      .replace(/\t/g, ' ')
      .replace(/\u00a0/g, ' ')
      .trimEnd()
      .trimStart()
      .replace(/[ ]{2,}/g, ' ');
    if (!normalizedLine) {
      if (!lastWasBlank) out.push('');
      lastWasBlank = true;
      return;
    }
    out.push(normalizedLine);
    lastWasBlank = false;
  });

  while (out.length && !out[0]) out.shift();
  while (out.length && !out[out.length - 1]) out.pop();
  return out.join('\n');
};

const splitDelimitedBlocks = (text = '') =>
  normalizeLineBreaks(text)
    .replace(/\f/g, '\n\n\n')
    .split(/\n\s*(?:---+|===+|___+|\*{3,}|#{3,}|[—–]{1,})\s*\n/g)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

const splitOnLargeGaps = (text = '') =>
  normalizeLineBreaks(text)
    .replace(/\f/g, '\n\n\n')
    .split(/\n{3,}/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

const splitByUnderscoreTitleBoundaries = (text = '') => {
  const lines = normalizeLineBreaks(text).split('\n');
  const starts = [];
  for (let i = 0; i < lines.length; i += 1) {
    const trimmed = lines[i].trim();
    if (!trimmed) continue;
    if (/_\s*$/.test(trimmed) && trimmed.length > 1) starts.push(i);
  }
  if (starts.length < 2) return null;
  const blocks = [];
  for (let i = 0; i < starts.length; i += 1) {
    const start = starts[i];
    const end = starts[i + 1] ?? lines.length;
    const block = lines.slice(start, end).join('\n').trim();
    if (block) blocks.push(block);
  }
  return blocks.length ? blocks : null;
};

const parseSingleBlock = (block = '') => {
  const rawLines = normalizeLineBreaks(block).split('\n');
  const lines = rawLines.map((line) => line.trimEnd());
  const nonEmpty = lines.map((line, idx) => ({ line: line.trim(), idx })).filter((entry) => entry.line);
  if (!nonEmpty.length) return null;

  let title = '';
  const titleLine = nonEmpty.find((entry) => /_\s*$/.test(entry.line));
  if (titleLine) {
    title = titleLine.line.replace(/_+\s*$/, '').trim();
    lines[titleLine.idx] = '';
  }

  const tags = [];
  lines.forEach((line, idx) => {
    const parsedTags = parseKwTags(line);
    if (parsedTags.length) {
      tags.push(...parsedTags);
      lines[idx] = '';
    }
  });

  const createdAtIso = extractFirstDateIso(lines.join('\n'));
  if (createdAtIso) {
    const dateLineIdx = lines.findIndex((line) => parseDateTokenToIso(line));
    if (dateLineIdx >= 0) lines[dateLineIdx] = '';
  }
  const notes = normalizeNoteBody(lines);
  if (!title) {
    const fallback = nonEmpty
      .map((entry) => lines[entry.idx].trim())
      .find((line) => line && !looksLikeDate(line) && !/^kw::/i.test(line));
    title = fallback ? fallback.slice(0, 120) : '';
  }

  return {
    title,
    notes,
    tags: [...new Set(tags)],
    createdAtIso
  };
};

export const parseBulkNoteText = (value = '') => {
  const text = normalizeLineBreaks(value).trim();
  if (!text) return [];

  const underscoreBlocks = splitByUnderscoreTitleBoundaries(text);
  if (underscoreBlocks?.length) return underscoreBlocks.map(parseSingleBlock).filter(Boolean);

  const delimited = splitDelimitedBlocks(text);
  if (delimited.length > 1) return delimited.map(parseSingleBlock).filter(Boolean);

  const byGaps = splitOnLargeGaps(text);
  if (byGaps.length > 1) return byGaps.map(parseSingleBlock).filter(Boolean);

  return [parseSingleBlock(text)].filter(Boolean);
};
